import React from "react";
import { makeStyles, createStyles, Theme, useTheme } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import useScrollTrigger from "@material-ui/core/useScrollTrigger";
import Slide from "@material-ui/core/Slide";
import HomeLogo from "components/HomeLogo";
import List from "@material-ui/core/List";
import routes from "routes";
import NavigationItem from "./NavigationItem";
import INavigationItem from "./models/INavigationItem";
import Rotate from "components/rotate/Rotate";
import { navBarColour, cssImportant, transparentColour } from "theme";
import IconButton from "@material-ui/core/IconButton";
import MenuIcon from "@material-ui/icons/Menu";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import Spacer from "components/Spacer";
import { useMediaQuery } from "@material-ui/core";
import background from "assets/images/navigationBar/nav_bg.png";
import RouterAnchorLinkButton from "components/buttons/RouterAnchorLinkButton";

import homeSprite from "assets/images/navigationBar/navigationItemSprites/nav_home.png";
import newsSprite from "assets/images/navigationBar/navigationItemSprites/nav_news.png";
import getAnchorRoute from "utils/getAnchorRoute";

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        root: {
            flex: "1 1 auto",
            display: "flex",
            justifyContent: "space-between",
        },
        menuButton: {
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flex: 1,
            color: theme.palette.common.white,
            [theme.breakpoints.up("md")]: {
                display: "none",
            },
        },
        desktopNavItemsContainer: {
            display: "flex",
            [theme.breakpoints.down("sm")]: {
                display: "none",
            },
        },
        mobileNavItemsContainer: {
            display: "flex",
            flexDirection: "column",
        },
        homeLogoContainer: {
            position: "absolute",
        },
        drawer: {
            zIndex: ((theme.zIndex.appBar - 1 + cssImportant) as unknown) as number,
        },
        drawerPaper: {
            backgroundColor: navBarColour,
            borderBottom: `3px solid ${theme.palette.common.white}`,
        },
    });
});

export default function NavigationBar() {
    const classes = useStyles();
    const theme = useTheme();
    const mdScreen = useMediaQuery(theme.breakpoints.down("md"));
    const smScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const xsScreen = useMediaQuery(theme.breakpoints.down("xs"));
    const xxsScreen = useMediaQuery("@media (max-width:400px)");

    const hideAppBarTrigger = useScrollTrigger();

    const navItems: INavigationItem[] = [
        {
            spriteSrc: homeSprite,
            label: "Home",
            href: getAnchorRoute(routes.home.route, routes.home.anchors.root),
            matchExactPath: true,
        },
        {
            spriteSrc: newsSprite,
            label: "News",
            href: routes.news.route,
        },
    ];

    const [mobileOpen, setMobileOpen] = React.useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const homeLogoWidth = xxsScreen ? 130 : xsScreen ? 165 : smScreen ? 200 : 225;

    const buyNowButton = (
        <div style={{ display: "flex", alignItems: "center" }}>
            <Rotate amount={-1}>
                <RouterAnchorLinkButton
                    route={routes.home.route}
                    anchor={routes.home.anchors.buy}
                    onClick={() => setMobileOpen(false)}
                >
                    Buy Now
                </RouterAnchorLinkButton>
            </Rotate>
        </div>
    );

    return (
        <Slide
            appear={false}
            direction="down"
            in={!hideAppBarTrigger}
            style={{
                backgroundColor: transparentColour,
                backgroundImage: `url(${background})`,
                backgroundSize: "101% 102%",
                backgroundPositionX: "center",
            }}
        >
            <AppBar elevation={0} color="inherit">
                <Toolbar style={{ alignItems: "unset" }}>
                    <div className={classes.root}>
                        <div className={classes.menuButton}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                            >
                                <MenuIcon />
                            </IconButton>
                        </div>

                        <div
                            style={{
                                width: homeLogoWidth,
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div className={classes.homeLogoContainer}>
                                <Rotate amount={-3}>
                                    <div style={{ zIndex: 20 }}>
                                        <HomeLogo size={homeLogoWidth} />
                                    </div>
                                </Rotate>
                            </div>
                        </div>

                        <List className={classes.desktopNavItemsContainer} disablePadding>
                            {navItems.map((item, i) => {
                                return (
                                    <React.Fragment key={i}>
                                        <NavigationItem item={item} />
                                    </React.Fragment>
                                );
                            })}
                            {buyNowButton}
                        </List>

                        <div
                            style={{
                                flex: "1",
                            }}
                        ></div>
                    </div>
                </Toolbar>
                <Hidden smUp implementation="css">
                    <Drawer
                        variant="temporary"
                        anchor="top"
                        open={mobileOpen && mdScreen}
                        onClose={handleDrawerToggle}
                        classes={{
                            root: classes.drawer,
                            paper: classes.drawerPaper,
                        }}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                        transitionDuration={theme.transitions.duration.standard}
                    >
                        <Toolbar />
                        <Spacer size={6} />
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                flex: "1",
                            }}
                        >
                            <List className={classes.mobileNavItemsContainer} disablePadding>
                                {navItems.map((item, i) => {
                                    return (
                                        <React.Fragment key={i}>
                                            <NavigationItem
                                                item={item}
                                                onClick={() => setMobileOpen(false)}
                                            />
                                        </React.Fragment>
                                    );
                                })}
                                {buyNowButton}
                            </List>
                        </div>
                        <Spacer />
                    </Drawer>
                </Hidden>
            </AppBar>
        </Slide>
    );
}
